import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ArrowLeftCircle, CheckCircle2, XCircle,
  Clock, GraduationCap, History, Loader2, PlayCircle, Trophy, Award,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const fmt2 = (n) => String(n).padStart(2, "0");
const formatClock = (sec) => {
  if (sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${fmt2(m)}:${fmt2(s)}`;
};

function IntroScreen({ onStart, busy, history }) {
  const downloadCert = async (attemptId) => {
    try {
      const resp = await api.get(
        `/quiz/attempts/${attemptId}/certificate`,
        { responseType: "blob", timeout: 60_000 }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Novatus_Zertifikat.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zertifikat-Download fehlgeschlagen");
    }
  };
  return (
    <div className="space-y-5">
      <section className="bg-[#0F1014] border border-[#00E575]/25 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#00E575]/15 border border-[#00E575]/30 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="text-[#00E575]" size={22} />
          </div>
          <div className="text-[13px] text-zinc-300 leading-relaxed">
            <p className="mb-2">
              <b className="text-zinc-100">Wärmepumpen-Wissens-Check.</b> 30 Multiple-Choice-Fragen aus den
              15 Schulungs-Kapiteln des Handbuchs (Teil VIII). Pro Frage genau eine richtige Antwort.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Zeitlimit: <b className="text-zinc-100">25 Minuten</b> – Countdown läuft sichtbar mit.</li>
              <li>Bestehen: <b className="text-[#00E575]">24 von 30</b> richtige Antworten (80 %).</li>
              <li>Fragen UND Antworten erscheinen in zufälliger Reihenfolge.</li>
              <li>Du kannst beliebig oft teilnehmen – jeder Versuch wird gespeichert und an deinen Vertriebsdirektor übermittelt.</li>
            </ul>
          </div>
        </div>
      </section>

      <button
        onClick={onStart}
        disabled={busy}
        data-testid="quiz-start-btn"
        className="w-full h-14 rounded-2xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:opacity-50 active:scale-[0.99] transition text-[15px]"
      >
        {busy ? (
          <><Loader2 className="animate-spin" size={18} /> Quiz wird vorbereitet…</>
        ) : (
          <><PlayCircle size={20} /> Quiz starten</>
        )}
      </button>

      {history.length > 0 && (
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <History size={16} className="text-zinc-400" />
            <h2 className="font-display text-base font-bold">Meine Versuche</h2>
          </div>
          <div className="space-y-2">
            {history.map((a) => (
              <div
                key={a.id}
                data-testid={`history-row-${a.id}`}
                className="flex items-center justify-between bg-[#0F1014] border border-zinc-800 rounded-xl px-4 py-3"
              >
                <div className="text-[12.5px]">
                  <div className="font-mono text-zinc-300">
                    {new Date(a.started_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-[10.5px] text-zinc-500">
                    {a.status === "in_progress" ? "abgebrochen" : a.status === "submitted_late" ? "verspätet abgegeben" : "abgeschlossen"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {a.score !== null && a.score !== undefined ? (
                    <div className={`font-mono text-[15px] font-bold ${a.passed ? "text-[#00E575]" : "text-amber-400"}`}>
                      {a.score}/{a.total}
                    </div>
                  ) : (
                    <div className="text-[12px] text-zinc-400 font-medium">—</div>
                  )}
                  {a.passed === true && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-bold bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/30">
                      <Trophy size={11} /> bestanden
                    </span>
                  )}
                  {a.passed === false && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-bold bg-zinc-800 text-zinc-400 border border-zinc-700">
                      nicht bestanden
                    </span>
                  )}
                  {a.passed === true && (
                    <button
                      onClick={() => downloadCert(a.id)}
                      data-testid={`history-cert-${a.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-bold bg-[#00E575]/10 text-[#00E575] border border-[#00E575]/40 hover:bg-[#00E575]/20 active:scale-[0.98] transition"
                      title="Zertifikat als PDF herunterladen"
                    >
                      <Award size={11} /> PDF
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function RunningScreen({ attempt, onSubmit }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({}); // q_pos -> selected_idx
  const [secondsLeft, setSecondsLeft] = useState(attempt.time_limit_seconds);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  // Countdown
  useEffect(() => {
    const startMs = Date.parse(attempt.started_at);
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startMs) / 1000);
      const left = attempt.time_limit_seconds - elapsed;
      setSecondsLeft(left);
      if (left <= 0 && !submittedRef.current) {
        submittedRef.current = true;
        submitAttempt(true);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt.started_at]);

  const total = attempt.questions.length;
  const q = attempt.questions[current];
  const selected = answers[q.q_pos];
  const answeredCount = Object.keys(answers).length;

  const submitAttempt = async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      answers: Object.entries(answers).map(([q_pos, selected_idx]) => ({
        q_pos: Number(q_pos),
        selected_idx,
      })),
    };
    try {
      const { data } = await api.post(`/quiz/${attempt.attempt_id}/submit`, payload);
      if (auto) toast.info("Zeit abgelaufen – Quiz wurde automatisch abgegeben.");
      onSubmit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abgabe fehlgeschlagen");
      setSubmitting(false);
      submittedRef.current = false;
    }
  };

  const select = (idx) => {
    setAnswers((prev) => ({ ...prev, [q.q_pos]: idx }));
  };

  const goPrev = () => setCurrent((c) => Math.max(0, c - 1));
  const goNext = () => setCurrent((c) => Math.min(total - 1, c + 1));

  const timeWarning = secondsLeft <= 60;

  return (
    <div className="space-y-4">
      {/* Sticky timer + progress */}
      <div className="sticky top-0 z-10 bg-[#09090B]/95 backdrop-blur border-b border-zinc-800 -mx-4 px-4 py-3" data-testid="quiz-progress-bar">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Clock size={16} className={timeWarning ? "text-red-400" : "text-zinc-400"} />
            <div
              data-testid="quiz-countdown"
              className={`font-mono text-[16px] font-bold tabular-nums ${timeWarning ? "text-red-400 animate-pulse" : "text-zinc-100"}`}
            >
              {formatClock(secondsLeft)}
            </div>
          </div>
          <div className="text-[12px] text-zinc-300 font-medium">
            Frage <span className="font-bold text-zinc-100">{current + 1}</span> / {total} · {answeredCount} beantwortet
          </div>
        </div>
        <div className="mt-2 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#00E575] transition-all"
            style={{ width: `${((current + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
        <div className="text-[10.5px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
          Frage {current + 1}
        </div>
        <h2 className="font-display text-lg font-bold leading-snug mb-5" data-testid="quiz-question">
          {q.question}
        </h2>
        <div className="space-y-2">
          {q.options.map((opt, idx) => {
            const isSelected = selected === idx;
            const letters = ["A", "B", "C", "D"];
            return (
              <button
                key={idx}
                onClick={() => select(idx)}
                data-testid={`quiz-option-${idx}`}
                className={`w-full text-left rounded-xl border px-4 py-3 transition active:scale-[0.99] flex items-start gap-3 ${
                  isSelected
                    ? "bg-[#00E575]/10 border-[#00E575] text-zinc-100"
                    : "bg-[#0F1014] border-zinc-700 text-zinc-200 hover:border-zinc-500"
                }`}
              >
                <span className={`flex-shrink-0 h-7 w-7 rounded-lg flex items-center justify-center font-bold text-[13px] ${
                  isSelected ? "bg-[#00E575] text-[#09090B]" : "bg-zinc-800 text-zinc-400"
                }`}>
                  {letters[idx]}
                </span>
                <span className="text-[14px] leading-snug pt-0.5">{opt}</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="flex items-center gap-2">
        <button
          onClick={goPrev}
          disabled={current === 0}
          data-testid="quiz-prev-btn"
          className="h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-200 hover:border-zinc-500 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <ArrowLeftCircle size={18} /> Zurück
        </button>
        {current < total - 1 ? (
          <button
            onClick={goNext}
            data-testid="quiz-next-btn"
            className="flex-1 h-12 rounded-xl bg-zinc-100 text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-white active:scale-[0.99] transition"
          >
            Weiter <ArrowRight size={18} />
          </button>
        ) : (
          <button
            onClick={() => submitAttempt(false)}
            disabled={submitting}
            data-testid="quiz-submit-btn"
            className="flex-1 h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:opacity-50 active:scale-[0.99] transition"
          >
            {submitting ? (
              <><Loader2 className="animate-spin" size={18} /> Wird ausgewertet…</>
            ) : (
              <>Quiz abgeben ({answeredCount}/{total} beantwortet)</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function ResultScreen({ result, onRetake, onBack }) {
  const passed = result.passed;
  const percentage = Math.round((result.score / result.total) * 100);
  const [downloading, setDownloading] = useState(false);

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(
        `/quiz/attempts/${result.attempt_id}/certificate`,
        { responseType: "blob", timeout: 60_000 }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Novatus_Zertifikat.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Zertifikat heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zertifikat konnte nicht geladen werden");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      <section
        data-testid="quiz-result-summary"
        className={`rounded-2xl p-6 border-2 ${
          passed
            ? "bg-[#00E575]/10 border-[#00E575]"
            : "bg-amber-500/10 border-amber-500/60"
        }`}
      >
        <div className="flex items-start gap-4">
          {passed ? (
            <Trophy size={48} className="text-[#00E575] flex-shrink-0" />
          ) : (
            <XCircle size={48} className="text-amber-400 flex-shrink-0" />
          )}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold opacity-70">
              {passed ? "Bestanden" : "Nicht bestanden"}
            </div>
            <div className="font-display text-4xl font-bold mt-1" data-testid="quiz-result-score">
              {result.score} / {result.total}
            </div>
            <div className="text-[14px] text-zinc-300 mt-2">
              {percentage} % richtig · Bestehensgrenze {result.pass_threshold}/{result.total}
            </div>
            {result.time_over && (
              <div className="text-[12px] text-red-400 mt-2 font-medium">
                Hinweis: Versuch wurde nach Ablauf der Zeit abgegeben.
              </div>
            )}
          </div>
        </div>
        {passed && (
          <button
            onClick={downloadCertificate}
            disabled={downloading}
            data-testid="quiz-certificate-btn"
            className="mt-5 w-full h-12 rounded-xl bg-[#09090B] text-[#00E575] border-2 border-[#00E575] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00E575] hover:text-[#09090B] disabled:opacity-50 active:scale-[0.99] transition"
          >
            {downloading ? (
              <><Loader2 className="animate-spin" size={18} /> Erzeuge Zertifikat…</>
            ) : (
              <><Award size={18} /> Zertifikat als PDF herunterladen</>
            )}
          </button>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-lg font-bold">Auswertung im Detail</h2>
        {result.detail.map((d, i) => {
          const letters = ["A", "B", "C", "D"];
          return (
            <div
              key={i}
              data-testid={`quiz-result-q-${i}`}
              className={`rounded-2xl border p-4 ${
                d.is_correct
                  ? "bg-[#0F1014] border-[#00E575]/40"
                  : "bg-[#0F1014] border-red-500/40"
              }`}
            >
              <div className="flex items-start gap-2 mb-3">
                {d.is_correct ? (
                  <CheckCircle2 size={18} className="text-[#00E575] flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="font-bold text-[14px] leading-snug">{d.question}</div>
              </div>
              <div className="space-y-1.5 ml-6">
                {d.options.map((opt, idx) => {
                  const isCorrect = idx === d.correct_idx;
                  const isSelected = idx === d.selected_idx;
                  let style = "bg-[#18181B] border-zinc-800 text-zinc-400";
                  if (isCorrect) style = "bg-[#00E575]/10 border-[#00E575]/60 text-[#00E575] font-medium";
                  else if (isSelected) style = "bg-red-500/10 border-red-500/50 text-red-300";
                  return (
                    <div key={idx} className={`rounded-lg border px-3 py-2 text-[12.5px] flex items-start gap-2 ${style}`}>
                      <span className="font-mono font-bold flex-shrink-0">{letters[idx]}</span>
                      <span className="leading-snug">{opt}</span>
                      {isCorrect && <span className="ml-auto text-[12px] font-bold uppercase tracking-wider">richtig</span>}
                      {isSelected && !isCorrect && <span className="ml-auto text-[12px] font-bold uppercase tracking-wider">deine Wahl</span>}
                    </div>
                  );
                })}
              </div>
              {d.explanation && (
                <div className="mt-3 ml-6 text-[12px] text-zinc-400 italic leading-relaxed border-l-2 border-zinc-700 pl-3 font-medium">
                  {d.explanation}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <button
          onClick={onBack}
          data-testid="quiz-result-back-btn"
          className="h-12 px-5 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-200 hover:border-zinc-500 inline-flex items-center justify-center gap-2"
        >
          <ArrowLeft size={16} /> Zurück zum Dashboard
        </button>
        <button
          onClick={onRetake}
          data-testid="quiz-result-retake-btn"
          className="flex-1 h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] active:scale-[0.99] transition"
        >
          <PlayCircle size={18} /> Erneut versuchen
        </button>
      </div>
    </div>
  );
}

export default function QuizPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [phase, setPhase] = useState("intro"); // intro | running | result
  const [attempt, setAttempt] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/quiz/my-attempts");
      setHistory(data.attempts || []);
    } catch (e) {
      // Silent: history is optional
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const startQuiz = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/quiz/start");
      setAttempt(data);
      setResult(null);
      setPhase("running");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quiz konnte nicht gestartet werden");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitted = (data) => {
    setResult(data);
    setPhase("result");
    loadHistory();
  };

  const header = useMemo(() => {
    const titles = { intro: "Schulungsquiz", running: "Quiz läuft", result: "Ergebnis" };
    return titles[phase];
  }, [phase]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 px-4 py-5 max-w-3xl mx-auto" data-testid="quiz-page">
      <header className="flex items-center gap-3 mb-6">
        {phase !== "running" && (
          <button
            onClick={() => navigate("/dashboard")}
            className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
            data-testid="quiz-back-btn"
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#00E575]/80 font-bold">
            Wärmepumpen-Schulung
          </div>
          <h1 className="font-display text-2xl font-bold leading-tight">{header}</h1>
        </div>
      </header>

      {phase === "intro" && (
        <IntroScreen onStart={startQuiz} busy={busy} history={history} />
      )}
      {phase === "running" && attempt && (
        <RunningScreen attempt={attempt} onSubmit={handleSubmitted} />
      )}
      {phase === "result" && result && (
        <ResultScreen
          result={result}
          onBack={() => navigate("/dashboard")}
          onRetake={() => { setResult(null); setAttempt(null); setPhase("intro"); }}
        />
      )}
    </div>
  );
}
